<!--
@component
NextMatchesWidgetMatch

A single match card used inside the matches widget rail. The markup
is composed from a handful of `{#snippet}` blocks so the three rows
(home team, away team, schedule/score sidebar) and their conditional
score / kickoff fragments live in one place each instead of being
duplicated inline.
-->

<script lang="ts">
  import {
    getAwayScore,
    getAwayTeam,
    getHomeScore,
    getHomeTeam,
    isPlaying,
    hasPlayed,
    isUpcoming,
  } from '$utils/getMatchStats';
  import { appState } from '$lib/stores/index.svelte';
  import { isMatchSoon } from '$utils/dates';

  import getMatchPath from '$utils/getMatchPath';
  import AnimatedScore from '$lib/components/common/AnimatedScore/index.svelte';
  import Flag from '$lib/components/common/Flag/index.svelte';
  import LiveBug from '$lib/components/common/LiveBug/index.svelte';
  import { onMount } from 'svelte';

  // NOTE: `match` stays as `any` because the rest of the codebase's
  // match helpers are typed loosely. Tightening to `ContestedMatch` here
  // cascades into errors in `getMatchStats` and related utils.
  let { match }: { match: any } = $props();

  const path = $derived(getMatchPath(match));
  const isLive = $derived(isPlaying(match));
  const isFinished = $derived(hasPlayed(match));
  // `isUpcoming` returns true for matches that haven't happened yet —
  // i.e. *not* played. We invert it so the variable name reads true.
  const isPlayed = $derived(!isUpcoming(match) && !isLive);

  // Pre-formatted `{ month, day, time }` decorated onto each match by
  // `getData()` (see `$utils/data/endpoints.js`) — no per-render date math.
  const date = $derived(match.localDateStamp);

  const homeTeam = $derived(getHomeTeam(match));
  const homeScore = $derived(getHomeScore(match));
  const awayTeam = $derived(getAwayTeam(match));
  const awayScore = $derived(getAwayScore(match));

  /** Determine Lead score */
  const leadScore = $derived(
    homeScore?.score > awayScore?.score ? homeScore?.score : homeScore?.score
  );

  /** Show match score for live and completed matches. */
  const showScore = $derived(isLive || isPlayed);

  // Show only the kickoff time (no date) when the match is today, or when
  // it kicks off in under 12 hours.
  const showTimeOnly = $derived(isMatchSoon(match));

  // The `localDateStamp` decoration is computed in the viewer's
  // timezone, so SSR and the hydrated client render can disagree. Show
  // `…` until the component has mounted to avoid a visible flash/shift.
  let mounted = $state(false);
  onMount(() => {
    mounted = true;
  });

  /** True when we can safely render formatted date strings client-side. */
  const dateReady = $derived(mounted && !appState.isLoading && !!date);
</script>

<!---------------->
<!-- Snippets ---->
<!---------------->

{#snippet score(
  value: { score?: number | string; pen?: number | string } | undefined | null
)}
  <div class="score fml-3 text-sm flex items-center">
    <AnimatedScore value={value?.score ?? 0} />
    {#if value?.pen}
      <span class="text-xxs ml-0.5">({value.pen})</span>
    {/if}
  </div>
{/snippet}

{#snippet teamRow(
  rowClass: 'home' | 'away',
  team: { name?: string; code?: string } | undefined | null,
  rowScore:
    | { score?: number | string; pen?: number | string }
    | undefined
    | null
)}
  <div
    class="row {rowClass} {rowClass === 'home' ? 'my-0.5' : (
      'my-1'
    )} flex justify-between"
    class:lead={homeScore?.score !== awayScore?.score &&
      leadScore === rowScore?.score}
  >
    <div class="team flex items-center">
      <div class="flag relative" class:loading={appState.isLoading}>
        <Flag width="var(--theme-font-size-base)" country={team?.name} />
      </div>
      <span class="fml-1 text-xs font-medium">{team?.code}</span>
    </div>

    {#if showScore}
      {@render score(rowScore)}
    {:else}
      <div class="empty">-</div>
    {/if}
  </div>
{/snippet}

{#snippet kickoff()}
  <div
    class="schedule flex flex-col fml-3 text-center font-medium text-secondary items-center"
  >
    {#if showTimeOnly}
      <div class="time text-xs text-primary">
        {#if dateReady}
          {date.time}
        {:else}
          &hellip;
        {/if}
      </div>
    {:else}
      <div class="date text-xs uppercase tracking-wide">
        {#if dateReady}
          {date.month}
          {date.day}
        {:else}
          &hellip;
        {/if}
      </div>
      <div class="time text-xxs">
        {#if dateReady}
          {date.time}
        {:else}
          &hellip;
        {/if}
      </div>
    {/if}
  </div>
{/snippet}

<!---------------->
<!-- Match card -->
<!---------------->
<!--
  IMPORTANT: only set `target` when the widget is actually embedded inside
  an iframe. SvelteKit's client-side router checks for the presence of a
  `target` attribute on `<a>` and bails out of intercepting the click if
  one is set — even when the value is `_self`.
-->
<a
  href={path?.url}
  style:width="calc(100% / var(--matchCount))"
  class:loading={appState.isLoading}
  target={appState.embed ? '_top' : undefined}
>
  <div
    class="match flex items-center justify-evenly relative fpx-3 fpy-2"
    title={path?.label?.toUpperCase()}
    class:live={isLive}
    class:played={isPlayed}
  >
    {#if isLive}
      <LiveBug />
    {/if}

    <div class="teams flex flex-col">
      {@render teamRow('home', homeTeam, homeScore)}
      {@render teamRow('away', awayTeam, awayScore)}
    </div>

    {#if !isLive && !isPlayed}
      {@render kickoff()}
    {:else if !isLive}
      <div class="full-time">Full Time</div>
    {/if}
  </div>
</a>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  a {
    text-decoration: none;
    display: block;
    position: relative;
    max-width: 175px;
    height: 65px;
    &.loading {
      pointer-events: none;
      &:before {
        content: '';
        position: absolute;
        z-index: 1;
        left: 0;
        top: 0;
        border-radius: var(--theme-style-border-radius);
        mix-blend-mode: multiply;
        width: 100%;
        height: 100%;
        @include mixins.loading;
      }
    }

    // When there are 2+ matches, draw a divider between adjacent cards.
    // Using border-right on every card except the last gives a single
    // separator regardless of how many matches are rendered (works for
    // 2, 3, 4+ matches).
    &:not(:only-child):not(:last-child) {
      border-right: 1px solid var(--theme-colour-tan-grey);
    }

    // Round the outer corners only on the leading/trailing cards. When
    // there's a single match (`:only-child`), both selectors apply so all
    // four corners are rounded.
    &:first-child {
      .match {
        border-top-left-radius: calc(1 * var(--theme-style-border-radius));
        border-bottom-left-radius: calc(1 * var(--theme-style-border-radius));
      }
    }
    &:last-child {
      .match {
        border-top-right-radius: calc(1 * var(--theme-style-border-radius));
        border-bottom-right-radius: calc(1 * var(--theme-style-border-radius));
      }
    }
  }

  .match {
    height: 100%;
    width: 100%;
    background-color: var(--theme-colour-muted-grey);
    scroll-snap-align: center;

    @include mixins.hover-supported() {
      opacity: 0.9;
      text-decoration: inherit;
      color: inherit;
    }

    &.live {
      .score {
        color: var(--theme-colour-live-red);
        @include mixins.font-medium;
      }
    }
  }

  .row {
    grid-row-gap: 1ch;
  }

  .score {
    font-variant-numeric: tabular-nums;
  }

  .flag {
    width: var(--theme-font-size-base);
    outline: 1px solid var(--theme-colour-white);
    aspect-ratio: 3/2;

    // `Flag` already renders the inlined base64 fallback PNG until the
    // real flag has decoded, so we don't need another network-fetched
    // `UNKNOWN.png` underneath the `<img>`.
    &.loading {
      background-color: var(--theme-colour-tan-grey);
    }
  }
</style>
